import { action, computed, makeObservable, observable } from 'mobx';
import DisturbanceApi from '@api/DisturbanceApi';
import DisturbanceMapStore from '@stores/DisturbanceMapStore';
import DisturbanceLocationModalStore from '@stores/DisturbanceLocationModalStore';
import RootStore from '@stores/RootStore';
import SsoStore from '@stores/SsoStore';
import TranslateStore from '@stores/TranslateStore';
import { Disturbance, DisturbanceType } from '@config/DisturbanceConfig';
import { getAuthType } from '@util/ApiUtil';

export default class DisturbanceModalStore {
  @observable accessor disturbance: Disturbance;
  @observable accessor extraData: Disturbance;
  @observable accessor type: DisturbanceType;
  @observable accessor disturbanceId: string = null;
  @observable accessor modalIsOpen: boolean = false;
  @observable accessor isLoading: boolean = true;
  @observable accessor loadingError: boolean = false;
  @observable accessor scrollToId: string = null;

  private mapStoreModal: boolean = false;
  private locationStoreModal: boolean = false;
  private readonly disturbanceMapStore: DisturbanceMapStore;
  private readonly disturbanceLocationModalStore: DisturbanceLocationModalStore;
  private readonly translateStore: TranslateStore;
  private readonly ssoStore: SsoStore;

  public constructor(rootStore: RootStore) {
    makeObservable(this);
    this.disturbanceMapStore = rootStore.disturbanceMapStore;
    this.disturbanceLocationModalStore = rootStore.disturbanceLocationModalStore;
    this.translateStore = rootStore.translateStore;
    this.ssoStore = rootStore.ssoStore;
  }

  @action
  public async fetchDisturbance(disturbance: Disturbance): Promise<void> {
    await this.fetchData(async () => {
      if (disturbance.disturbanceId.length) {
        this.disturbance = disturbance;
        this.disturbanceId = disturbance.disturbanceId;
        this.extraData = await DisturbanceApi.getDisturbance(this.disturbanceId, getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
      }
    });
  }

  @action
  public async fetchDisturbanceById(id: string): Promise<void> {
    await this.fetchData(async () => {
      if (id) {
        const disturbance: Disturbance = await DisturbanceApi.getDisturbanceById(id, this.type, getAuthType(this.ssoStore.loggedIn, this.ssoStore.isDealer));
        this.disturbance = disturbance;
        this.disturbanceId = disturbance.disturbanceId;
        this.extraData = disturbance;
      }
    });
  }

  public async fetchData(method: any): Promise<void> {
    try {
      this.resetState();
      await method();
    } catch (err) {
      console.error(err);
      this.loadingError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  public async openModal(disturbance: Disturbance, type: DisturbanceType, scrollToId: string = null) {
    if (!this.modalIsOpen) {
      this.type = type;
      this.modalIsOpen = true;
      this.mapStoreModal = false;
      this.locationStoreModal = false;
      this.scrollToId = scrollToId ? scrollToId : null;
      if (this.disturbanceLocationModalStore.modalIsOpen) {
        this.locationStoreModal = true;
        this.disturbanceLocationModalStore.modalIsOpen = false;
        this.disturbance = disturbance;
        this.disturbanceId = disturbance.disturbanceId;
        this.extraData = disturbance;
        this.isLoading = false;
      } else if (disturbance.disturbanceId) {
        await this.fetchDisturbance(disturbance);
      } else if (this.disturbanceMapStore.modalIsOpen) {
        this.mapStoreModal = true;
        this.disturbanceMapStore.closeModal();
        await this.fetchDisturbanceById(disturbance.id);
      } else if (!disturbance.disturbanceId) {
        await this.fetchDisturbanceById(disturbance.id);
      }
    }
  }

  @action
  public closeModal() {
    this.modalIsOpen = false;
    this.disturbance = null;
    this.type = null;
    this.resetState();
    if (this.mapStoreModal) {
      this.mapStoreModal = false;
      this.disturbanceMapStore.openModal(this.scrollToId);
      this.scrollToId = null;
    } else if (this.locationStoreModal) {
      this.locationStoreModal = false;
      this.disturbanceLocationModalStore.continueModal();
      this.scrollToId = null;
    }
  }

  @computed
  public get getDuration(): string {
    if (this.extraData && this.extraData.riskOfDisruption && this.extraData.totalDurationOfDisruption !== null) {
      const hours = this.extraData.totalDurationOfDisruption >= 60 ? Math.floor(this.extraData.totalDurationOfDisruption / 60) : 0;
      const minutes = this.extraData.totalDurationOfDisruption - hours * 60;
      if (hours !== 0 || minutes !== 0) {
        const durationString = this.translateStore.translate('disturbance.disruption.yes') + ' ' +
          (hours !== 0 ? hours + this.translateStore.translate('disturbance.disruption.hour') : '') +
          (minutes !== 0 ? minutes + this.translateStore.translate('disturbance.disruption.min') : '');
        return durationString;
      }
    }
    return null;
  }

  private resetState = (): void => {
    this.extraData = null;
    this.isLoading = true;
    this.loadingError = false;
  };
}
